// ─────────────────────────────────────────────────────────────────────────────
// WorkoutContext (Web)
// Mirrors src/context/WorkoutContext.tsx in jas0nday/Crew-Z (React Native).
//
// Owns persistent workout state so it survives tab navigation. The PM5 byte
// parsing (lib/pm5Connection.js) is intentionally untouched — this context
// only listens to the window events that pm5Connection emits, and forwards
// the resulting metrics to React state.
// ─────────────────────────────────────────────────────────────────────────────

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { crewzBleService } from '../services/crewzBle';
import { rhythmRatio as computeRhythmRatio } from '../lib/rhythmRatio';
import useSessionOdometer from '../hooks/useSessionOdometer';

const STORAGE_KEY = 'activeWorkout';
// Max age for a persisted "active workout" — anything older than this is
// considered orphaned (browser closed mid-row, OS killed the tab, etc.) and
// must be discarded. This is the root fix for the "1217-hour session" bug
// where stale startTime values from days-old localStorage entries caused
// `elapsedTime` to be calculated against an ancient anchor and saved to
// the DB on the next Stop tap.
const MAX_PERSISTED_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours
// Hard safety cap on a single workout duration. Anything longer than this
// is a data-integrity bug, not a real row.
export const MAX_WORKOUT_DURATION_SECONDS = 6 * 60 * 60; // 6 hours
// CrewZ Session Data Model v1.6 §1: "Moving time only: The parent session
// timer increments only when speed exceeds 0.3 m/s." We keep both moving
// and elapsed clocks in state so downstream consumers can pick either.
export const MOVING_SPEED_THRESHOLD_MS = 0.3;

const readPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Reject stale sessions — if the persisted startTime is older than
    // MAX_PERSISTED_AGE_MS we discard it entirely so the user starts clean.
    if (parsed?.startTime) {
      const startMs = new Date(parsed.startTime).getTime();
      if (!Number.isFinite(startMs) || (Date.now() - startMs) > MAX_PERSISTED_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const WorkoutContext = createContext(null);

export function WorkoutProvider({ children }) {
  // Persisted state (rehydrated synchronously on first render)
  const persisted = (() => {
    const p = readPersisted();
    return p && p.active ? p : null;
  })();

  // ── Workout state ──────────────────────────────────────────────────────────
  const [workoutActive, setWorkoutActive] = useState(!!persisted);
  const [workoutPaused, setWorkoutPaused] = useState(!!persisted?.paused);
  const [elapsedTime, setElapsedTime] = useState(() => {
    if (persisted?.startTime) {
      const elapsed = Math.floor((Date.now() - new Date(persisted.startTime).getTime()) / 1000);
      return Math.max(0, elapsed);
    }
    return 0;
  });
  // PM5-authoritative elapsed clock (Mode 3 only). Distinct from elapsedTime
  // so ELAP always tracks the session wall-clock from Start→Stop, while
  // pm5_time tracks the PM5 monitor's own time-under-load. PM5-based
  // calculations that used to piggy-back on elapsedTime (e.g. avg split
  // = distance / pm5_elapsed) now read this value instead when a PM5 is
  // connected — matches how the C2 monitor itself computes its splits.
  const [pm5ElapsedTime, setPm5ElapsedTime] = useState(0);
  // Moving time — only advances when smoothedSpeed > MOVING_SPEED_THRESHOLD_MS
  // (or when the PM5 clock ticks, since the PM5 already gates on time-under-load).
  const [movingTime, setMovingTime] = useState(persisted?.movingTime || 0);
  const [distance, setDistance] = useState(persisted?.distance || 0);
  const [strokes, setStrokes] = useState(persisted?.strokes || 0);
  const [calories, setCalories] = useState(persisted?.calories || 0);
  const [targetDistance, setTargetDistance] = useState(persisted?.targetDistance || 2000);

  // ── Live metrics (not persisted; rebuild from BLE notifications) ───────────
  const [currentSplit, setCurrentSplit] = useState(0);
  const [strokeRate, setStrokeRate] = useState(0);
  const [heartRate, setHeartRate] = useState(0);
  const [watts, setWatts] = useState(0);
  const [metersPerSecond, setMetersPerSecond] = useState(0);
  const [smoothedSpeed, setSmoothedSpeed] = useState(0);
  // Rhythm Ratio (RHY) — recovery_ms / drive_ms. Producers in priority order:
  //   1. PM5  (char 0x0034 exposes driveTime + recoveryTime per stroke)
  //   2. IMU  (useStrokeDetection emits `crewz-rhythm-update`)
  //   3. NK EmPower — crewz-rhythm-update dispatched by NKEmpowerDriver.js (wired, active)
  const [rhythmRatio, setRhythmRatio] = useState(null);

  // ── Connection state ──────────────────────────────────────────────────────
  const [pm5Device, setPm5Device] = useState(() => {
    if (typeof window !== 'undefined' && window.pm5Connection?.isConnected) {
      return window.pm5Connection.device?.name || 'PM5';
    }
    return null;
  });
  const [crewzConnected, setCrewzConnected] = useState(false);
  const [crewzDeviceName, setCrewzDeviceName] = useState(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const workoutStartTimeRef = useRef(persisted?.startTime ? new Date(persisted.startTime) : null);
  const intervalRef = useRef(null);
  const seriesIntervalRef = useRef(null);
  const workoutDataRef = useRef({
    heartRateData: [], gpsData: [], strokeData: [], strokeArrays: [],
    // CrewZ Session Data Model v1.6 §3.3 time-series arrays. Each entry is
    // { t: <seconds since workout start>, v: <value> }.
    speedSeries: [], spmSeries: [], hrBpmSeries: [],
    wattsSeries: [], caloriesSeries: [], rhyRatioSeries: [],
    cfPctSeries: [],
  });
  // Sampler refs — kept in sync via useEffect below so the 1 Hz interval
  // reads always-fresh values without recreating the interval on every tick.
  const smoothedSpeedRef = useRef(0);
  const strokeRateRef = useRef(0);
  const heartRateRef = useRef(0);
  const wattsRef = useRef(0);
  // Displayed PWR published by the Monitor ({ v, t }) so the 1 Hz series records what the rower saw.
  const livePowerRef = useRef({ v: 0, t: 0 });
  const caloriesRef = useRef(0);
  const rhythmRatioRef = useRef(null);
  // Mirror flags for event handlers (avoid stale closures)
  const activeRef = useRef(workoutActive);
  // PM5 distance/time accumulator: the PM5 resets to 0 at the end of a piece or
  // when its menu is pressed — keep the metres already rowed instead of saving 0.
  const pm5OdoRef = useRef({ base: 0, last: 0, tBase: 0, tLast: 0 });
  const pausedRef = useRef(workoutPaused);
  useEffect(() => { activeRef.current = workoutActive; }, [workoutActive]);
  useEffect(() => { pausedRef.current = workoutPaused; }, [workoutPaused]);
  useSessionOdometer({ active: workoutActive, pm5Device, setDistance });

  // ── Persist core state to localStorage ────────────────────────────────────
  useEffect(() => {
    try {
      if (workoutActive) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          active: workoutActive,
          paused: workoutPaused,
          startTime: workoutStartTimeRef.current?.toISOString(),
          distance,
          strokes,
          calories,
          targetDistance,
          movingTime,
        }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      console.error('[WorkoutContext] persist error:', e);
    }
  }, [workoutActive, workoutPaused, distance, strokes, calories, targetDistance, movingTime]);

  // ── Elapsed-time tick (1 Hz) ──────────────────────────────────────────────
  // ELAP is the SESSION wall-clock: from the moment the rower taps Start to
  // the moment they tap Stop. It advances in every mode (including Mode 3)
  // whenever the workout is active + unpaused. The PM5-only clock lives on
  // pm5ElapsedTime and is driven separately by handlePM5RowingStatus below.
  useEffect(() => {
    if (workoutActive && !workoutPaused) {
      intervalRef.current = setInterval(() => {
        if (workoutStartTimeRef.current) {
          const elapsed = Math.floor((Date.now() - workoutStartTimeRef.current.getTime()) / 1000);
          setElapsedTime(prev => (elapsed > prev ? elapsed : prev + 1));
        } else {
          setElapsedTime(prev => prev + 1);
        }
        // CrewZ Session Data Model v1.6 §1 — moving-time gate. Only tick
        // movingTime forward when smoothedSpeed exceeds the 0.3 m/s
        // stationary threshold.
        if (smoothedSpeedRef.current > MOVING_SPEED_THRESHOLD_MS) {
          setMovingTime(prev => prev + 1);
        }
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [workoutActive, workoutPaused]);

  // ── Background catch-up: when tab becomes visible, advance elapsedTime ────
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden && activeRef.current && !pausedRef.current && workoutStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - workoutStartTimeRef.current.getTime()) / 1000);
        setElapsedTime(prev => (elapsed > prev ? elapsed : prev));
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // ── PM5 + HR window event listeners (live the lifetime of the provider) ───
  useEffect(() => {
    const handleHeartRateUpdate = (event) => {
      const hr = event.detail;
      setHeartRate(hr);
      if (activeRef.current && !pausedRef.current) {
        workoutDataRef.current.heartRateData.push({ timestamp: Date.now(), hr });
      }
    };

    const handlePM5RowingStatus = (event) => {
      const data = event.detail;
      // The PM5's time-under-load clock now lives on its own state
      // (pm5ElapsedTime). ELAP is the session wall-clock and is driven
      // by the 1 Hz interval above regardless of hardware. Distance
      // stays authoritative from PM5. The PM5 pauses its clock during
      // rest intervals, so we do NOT gate on pausedRef — the raw PM5
      // clock still flows so downstream avg-split maths stays in sync
      // with what the monitor itself displays.
      if (activeRef.current) {
        const elapsed = Number.isFinite(data.elapsedTime) ? Math.floor(data.elapsedTime) : 0;
        const d = Number.isFinite(data.distance) ? data.distance : 0;
        const o = pm5OdoRef.current;
        if (d + 5 < o.last) o.base += o.last;
        if (elapsed + 2 < o.tLast) o.tBase += o.tLast;
        o.last = d;
        o.tLast = elapsed;
        setPm5ElapsedTime(o.tBase + elapsed);
        setDistance(o.base + d);
      }
    };

    const handlePM5ExtraData = (event) => {
      const data = event.detail;
      if (activeRef.current && !pausedRef.current) {
        setStrokeRate(data.strokeRate);
        setWatts(data.power);
        setCalories(data.calories || 0);
        setCurrentSplit(data.pace);
        if (data.strokeCount !== undefined && data.strokeCount > 0) {
          setStrokes(data.strokeCount);
        }
        if (data.speed && data.speed > 0) {
          setMetersPerSecond(data.speed);
        }
        workoutDataRef.current.strokeData.push({
          timestamp: Date.now(),
          strokeRate: data.strokeRate,
          power: data.power,
          pace: data.pace,
          speed: data.speed,
          strokeCount: data.strokeCount,
        });
      }
    };

    const handlePM5Disconnect = () => {
      console.log('🚣 PM5 disconnected');
      setPm5Device(null);
    };

    const handlePM5Connect = (event) => {
      const name = event?.detail?.name || (window.pm5Connection?.device?.name) || 'PM5';
      setPm5Device(name);
    };

    // Rhythm Ratio — PM5 char 0x0034 reports drive/recovery in seconds.
    // We mark this source as PM5 so the IMU fallback can't overwrite it.
    const pm5RhythmActiveRef = { current: false };
    const handlePM5StrokeData = (event) => {
      if (!activeRef.current || pausedRef.current) return;
      const d = event?.detail;
      if (!d) return;
      const drive = (d.driveTime || 0) * 1000;          // s → ms
      const recovery = (d.recoveryTime || 0) * 1000;    // s → ms
      const ratio = computeRhythmRatio(drive, recovery);
      if (ratio != null) {
        pm5RhythmActiveRef.current = true;
        setRhythmRatio(ratio);
      }
    };

    // IMU / NK fallback — only honoured when PM5 hasn't been the rhythm source.
    const handleRhythmUpdate = (event) => {
      if (!activeRef.current || pausedRef.current) return;
      if (pm5RhythmActiveRef.current) return;   // PM5 owns this metric when present
      const d = event?.detail;
      if (d && typeof d.ratio === 'number' && Number.isFinite(d.ratio)) {
        setRhythmRatio(d.ratio);
      }
    };

    // Reset the PM5 rhythm latch whenever the PM5 disconnects — otherwise the
    // NK/IMU stream would stay permanently blocked after an earlier PM5 session.
    const handlePM5DisconnectResetRhythm = () => { pm5RhythmActiveRef.current = false; };

    // ── NK EmPower stroke-rate bridge ────────────────────────────────────────
    // Mode 4 (NK only, no PM5) — propagate `spm` from the gate frame into the
    // shared `strokeRate` state so the SPM tile + ½ fraction renderer works.
    // PM5 has priority: skip this update whenever PM5 is currently connected.
    const handleGateMetrics = (event) => {
      if (!activeRef.current || pausedRef.current) return;
      const d = event?.detail;
      if (!d || typeof d.spm !== 'number') return;
      // Only drive strokeRate from gates when no PM5 is bound.
      if (window.pm5Connection?.isConnected) return;
      if (d.spm > 0 && d.spm < 80) {
        setStrokeRate(d.spm);
      }
    };

    window.addEventListener('heart-rate-update', handleHeartRateUpdate);
    window.addEventListener('pm5-rowing-status', handlePM5RowingStatus);
    window.addEventListener('pm5-extra-rowing-data', handlePM5ExtraData);
    window.addEventListener('pm5-stroke-data', handlePM5StrokeData);
    window.addEventListener('pm5-disconnected', handlePM5Disconnect);
    window.addEventListener('pm5-disconnected', handlePM5DisconnectResetRhythm);
    window.addEventListener('pm5-connected', handlePM5Connect);
    window.addEventListener('crewz-rhythm-update', handleRhythmUpdate);
    window.addEventListener('crewz-gate-metrics', handleGateMetrics);

    return () => {
      window.removeEventListener('heart-rate-update', handleHeartRateUpdate);
      window.removeEventListener('pm5-rowing-status', handlePM5RowingStatus);
      window.removeEventListener('pm5-extra-rowing-data', handlePM5ExtraData);
      window.removeEventListener('pm5-stroke-data', handlePM5StrokeData);
      window.removeEventListener('pm5-disconnected', handlePM5Disconnect);
      window.removeEventListener('pm5-disconnected', handlePM5DisconnectResetRhythm);
      window.removeEventListener('pm5-connected', handlePM5Connect);
      window.removeEventListener('crewz-rhythm-update', handleRhythmUpdate);
      window.removeEventListener('crewz-gate-metrics', handleGateMetrics);
    };
  }, []);

  // ── EMA speed smoothing (alpha = 0.3) ─────────────────────────────────────
  // ── EMA speed smoothing (mode-aware α) ────────────────────────────────────
  // 1 Hz phone GPS is jittery — apply heavier smoothing in Mode 2 (no CrewZ
  // and no PM5) to suppress visible flicker. CrewZ/PM5-driven readings are
  // already high-rate and clean so the lighter α=0.3 stays.
  useEffect(() => {
    if (metersPerSecond === 0) {
      setSmoothedSpeed(0);
      return;
    }
    setSmoothedSpeed(prev => {
      if (prev === 0) return metersPerSecond;
      const isPhoneOnly = !crewzConnected && !pm5Device;
      const alpha = isPhoneOnly ? 0.15 : 0.3;
      return prev * (1 - alpha) + metersPerSecond * alpha;
    });
  }, [metersPerSecond, crewzConnected, pm5Device]);

  // ── CrewZ BLE hooks (forward-compatible; idle until firmware ships) ───────
  useEffect(() => {
    const onConnected = (device) => {
      setCrewzConnected(true);
      setCrewzDeviceName(device?.name || 'CrewZ');
    };
    const onDisconnected = () => {
      setCrewzConnected(false);
      setCrewzDeviceName(null);
    };
    crewzBleService.on('connected', onConnected);
    crewzBleService.on('disconnected', onDisconnected);
    return () => {
      crewzBleService.off('connected', onConnected);
      crewzBleService.off('disconnected', onDisconnected);
    };
  }, []);

  // ── Keep sampler refs in sync with state ─────────────────────────────────
  useEffect(() => { smoothedSpeedRef.current = smoothedSpeed; }, [smoothedSpeed]);
  useEffect(() => { strokeRateRef.current = strokeRate; }, [strokeRate]);
  useEffect(() => { heartRateRef.current = heartRate; }, [heartRate]);
  useEffect(() => { wattsRef.current = watts; }, [watts]);
  useEffect(() => { caloriesRef.current = calories; }, [calories]);
  useEffect(() => { rhythmRatioRef.current = rhythmRatio; }, [rhythmRatio]);

  // ── 1 Hz series sampler (CrewZ Session Data Model v1.6 §3.3) ─────────────
  // Decimated to 1 Hz for storage size + Strava/Intervals.icu export
  // compatibility. Full-resolution capture is a backlog item.
  useEffect(() => {
    if (workoutActive && !workoutPaused) {
      seriesIntervalRef.current = setInterval(() => {
        if (!workoutStartTimeRef.current) return;
        const tSec = Math.floor((Date.now() - workoutStartTimeRef.current.getTime()) / 1000);
        const s = workoutDataRef.current;
        // Always capture HR (per spec: 1 Hz strap in all modes).
        if (heartRateRef.current > 0) s.hrBpmSeries.push({ t: tSec, v: heartRateRef.current });
        // Motion-gated metrics per moving-time rule. Speed itself we capture
        // unconditionally so consumers can plot the full velocity trace
        // (including the stationary flats) if they want.
        s.speedSeries.push({ t: tSec, v: smoothedSpeedRef.current || 0 });
        if (strokeRateRef.current > 0) s.spmSeries.push({ t: tSec, v: strokeRateRef.current });
        const lp = livePowerRef.current;
        const pw = lp.v > 0 && Date.now() - lp.t < 3000 ? lp.v : wattsRef.current;
        if (pw > 0) s.wattsSeries.push({ t: tSec, v: pw });
        if (caloriesRef.current > 0) s.caloriesSeries.push({ t: tSec, v: caloriesRef.current });
        if (rhythmRatioRef.current != null && Number.isFinite(rhythmRatioRef.current)) {
          s.rhyRatioSeries.push({ t: tSec, v: rhythmRatioRef.current });
        }
      }, 1000);
    } else if (seriesIntervalRef.current) {
      clearInterval(seriesIntervalRef.current);
      seriesIntervalRef.current = null;
    }
    return () => {
      if (seriesIntervalRef.current) {
        clearInterval(seriesIntervalRef.current);
        seriesIntervalRef.current = null;
      }
    };
  }, [workoutActive, workoutPaused]);

  // ── crewz-cf-update listener — per-stroke Check Factor % samples ─────────
  useEffect(() => {
    const onCf = (event) => {
      if (!activeRef.current || pausedRef.current) return;
      const d = event?.detail;
      if (!d || typeof d.cfPct !== 'number' || !Number.isFinite(d.cfPct)) return;
      if (!workoutStartTimeRef.current) return;
      const tSec = Math.floor((Date.now() - workoutStartTimeRef.current.getTime()) / 1000);
      workoutDataRef.current.cfPctSeries.push({ t: tSec, v: d.cfPct });
    };
    window.addEventListener('crewz-cf-update', onCf);
    return () => window.removeEventListener('crewz-cf-update', onCf);
  }, []);

  // ── Stroke replay capture (forward-compat with BLE oarlock drivers) ───────
  // Listens for `crewz-gate-stroke` window events while a workout is active and
  // appends each per-stroke arrays payload into workoutDataRef so the entire
  // session can be replayed in WorkoutHistory after save.
  useEffect(() => {
    const onStroke = (event) => {
      if (!activeRef.current) return;
      const d = event?.detail;
      if (!d || !Array.isArray(d.forceN) || !Array.isArray(d.angleDeg)) return;
      if (!workoutDataRef.current.strokeArrays) workoutDataRef.current.strokeArrays = [];
      workoutDataRef.current.strokeArrays.push({
        i: d.strokeIndex ?? workoutDataRef.current.strokeArrays.length,
        t: d.timestamp || Date.now(),
        side: d.side || 'sweep',
        forceN: d.forceN,
        angleDeg: d.angleDeg,
        rigging: d.rigging || null,
        spm: typeof d.spm === 'number' ? d.spm : null,
      });
    };
    window.addEventListener('crewz-gate-stroke', onStroke);
    return () => window.removeEventListener('crewz-gate-stroke', onStroke);
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────────
  const startWorkout = useCallback((target) => {
    workoutStartTimeRef.current = new Date();
    setWorkoutActive(true);
    setWorkoutPaused(false);
    setElapsedTime(0);
    setPm5ElapsedTime(0);
    pm5OdoRef.current = { base: 0, last: 0, tBase: 0, tLast: 0 };
    setMovingTime(0);
    setDistance(0);
    setStrokes(0);
    setCalories(0);
    setCurrentSplit(0);
    setStrokeRate(0);
    setHeartRate(0);
    setWatts(0);
    setMetersPerSecond(0);
    setSmoothedSpeed(0);
    setRhythmRatio(null);
    if (typeof target === 'number') setTargetDistance(target);
    workoutDataRef.current = {
      heartRateData: [], gpsData: [], strokeData: [], strokeArrays: [],
      speedSeries: [], spmSeries: [], hrBpmSeries: [],
      wattsSeries: [], caloriesSeries: [], rhyRatioSeries: [], cfPctSeries: [],
    };
  }, []);

  const pauseWorkout = useCallback(() => setWorkoutPaused(true), []);
  const resumeWorkout = useCallback(() => setWorkoutPaused(false), []);
  const togglePause = useCallback(() => setWorkoutPaused(p => !p), []);

  const stopWorkout = useCallback(() => {
    const data = {
      startTime: workoutStartTimeRef.current,
      endTime: new Date(),
      duration: elapsedTime,
      movingTime,
      distance,
      strokes,
      calories,
      targetDistance,
      heartRateData: workoutDataRef.current.heartRateData,
      gpsData: workoutDataRef.current.gpsData,
      strokeData: workoutDataRef.current.strokeData,
      strokeArrays: workoutDataRef.current.strokeArrays || [],
      // CrewZ Session Data Model v1.6 §3.3 series
      speedSeries: workoutDataRef.current.speedSeries || [],
      spmSeries: workoutDataRef.current.spmSeries || [],
      hrBpmSeries: workoutDataRef.current.hrBpmSeries || [],
      wattsSeries: workoutDataRef.current.wattsSeries || [],
      caloriesSeries: workoutDataRef.current.caloriesSeries || [],
      rhyRatioSeries: workoutDataRef.current.rhyRatioSeries || [],
      cfPctSeries: workoutDataRef.current.cfPctSeries || [],
    };
    setWorkoutActive(false);
    setWorkoutPaused(false);
    workoutStartTimeRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return data;
  }, [elapsedTime, movingTime, distance, strokes, calories, targetDistance]);

  const resetWorkout = useCallback(() => {
    setWorkoutActive(false);
    setWorkoutPaused(false);
    setElapsedTime(0);
    setPm5ElapsedTime(0);
    pm5OdoRef.current = { base: 0, last: 0, tBase: 0, tLast: 0 };
    setMovingTime(0);
    setDistance(0);
    setStrokes(0);
    setCalories(0);
    setCurrentSplit(0);
    setStrokeRate(0);
    setHeartRate(0);
    setWatts(0);
    setMetersPerSecond(0);
    setSmoothedSpeed(0);
    setRhythmRatio(null);
    workoutStartTimeRef.current = null;
    workoutDataRef.current = {
      heartRateData: [], gpsData: [], strokeData: [], strokeArrays: [],
      speedSeries: [], spmSeries: [], hrBpmSeries: [],
      wattsSeries: [], caloriesSeries: [], rhyRatioSeries: [], cfPctSeries: [],
    };
    // Clear any persisted "active workout" so a ghost session can't
    // resurrect itself on the next mount (fixes the "session still showing
    // after log-in" issue reported on 2026-06-15).
    try {
      localStorage.removeItem('activeWorkout');
    } catch {
      /* localStorage unavailable — ignore */
    }
  }, []);

  const addDataPoint = useCallback((type, point) => {
    if (type === 'heartRate') workoutDataRef.current.heartRateData.push(point);
    else if (type === 'gps') workoutDataRef.current.gpsData.push(point);
    else if (type === 'stroke') workoutDataRef.current.strokeData.push(point);
  }, []);

  const value = {
    // Workout state
    workoutActive,
    setWorkoutActive,
    workoutPaused,
    setWorkoutPaused,
    elapsedTime,
    setElapsedTime,
    pm5ElapsedTime,
    setPm5ElapsedTime,
    movingTime,
    setMovingTime,
    distance,
    setDistance,
    strokes,
    setStrokes,
    calories,
    setCalories,
    targetDistance,
    setTargetDistance,

    // Live metrics
    currentSplit,
    setCurrentSplit,
    strokeRate,
    setStrokeRate,
    heartRate,
    setHeartRate,
    watts,
    setWatts,
    livePowerRef,
    metersPerSecond,
    setMetersPerSecond,
    smoothedSpeed,
    rhythmRatio,
    setRhythmRatio,

    // Connection state
    pm5Device,
    setPm5Device,
    crewzConnected,
    crewzDeviceName,

    // Refs
    workoutStartTimeRef,
    workoutDataRef,

    // Actions
    startWorkout,
    pauseWorkout,
    resumeWorkout,
    togglePause,
    stopWorkout,
    resetWorkout,
    addDataPoint,
  };

  return <WorkoutContext.Provider value={value}>{children}</WorkoutContext.Provider>;
}

export function useWorkout() {
  const ctx = useContext(WorkoutContext);
  if (!ctx) throw new Error('useWorkout must be used within a WorkoutProvider');
  return ctx;
}

export default WorkoutContext;

